import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../context/AuthContext";
import { Lock, CheckCircle } from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing token in URL"); return; }
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password reset! Sign in with your new password.");
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#0A0A0A" }}
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md mx-4 p-8 rounded-lg border border-white/10" style={{ background: "#121212" }}>
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} alt="VeloxAlgoTech LLP" className="w-16 h-16 mb-3" />
          <h1
            className="text-lg font-black tracking-tight text-center"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif", letterSpacing: "0.06em" }}
            data-testid="brand-name-reset"
          >
            <span style={{ color: '#00b4d8' }}>Velox</span>
            <span className="text-white">AlgoTech</span>
          </h1>
          <p className="text-sm font-bold text-white mt-2">Reset Password</p>
          <p className="text-xs text-[#A1A1AA] mt-1">Choose a new admin password</p>
        </div>

        {done ? (
          <div className="text-center py-6">
            <CheckCircle size={40} weight="duotone" className="text-emerald-400 mx-auto mb-3" />
            <p className="text-sm text-white font-bold">Password reset successful</p>
            <p className="text-xs text-[#A1A1AA] mt-1">Redirecting to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] mb-1 block">New Password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                <input
                  type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  required minLength={8}
                  placeholder="min 8 characters"
                  data-testid="reset-new-password"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-[#0F0F0F] border border-white/10 text-sm text-white placeholder-[#52525B] focus:outline-none focus:border-[#00E5FF]/40"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA] mb-1 block">Confirm Password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="reset-confirm-password"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-[#0F0F0F] border border-white/10 text-sm text-white placeholder-[#52525B] focus:outline-none focus:border-[#00E5FF]/40"
                />
              </div>
            </div>
            <button
              type="submit" disabled={submitting || !token}
              data-testid="reset-submit"
              className="w-full py-2.5 rounded-md text-sm font-bold text-black transition-all disabled:opacity-50"
              style={{ background: "#00E5FF" }}
            >
              {submitting ? "Resetting..." : "Reset Password"}
            </button>
            {!token && (
              <p className="text-[11px] text-rose-400 text-center">No token in URL. Use the reset link from your email.</p>
            )}
            <Link to="/login" className="block text-center text-[11px] text-[#A1A1AA] hover:text-white">
              Back to login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
